import type { Events } from '@setplex/pureya'
import type { IRewindMediaUrlParams } from '~/entities/player/event/index.h'
import type { IRewindState } from '~/features/rewind/useRewindState'
import type {
  CurrentEventMediaUrlWithEpg,
  CurrentProgramMediaUrl,
  CurrentTvMediaUrlWithEpg,
} from '~/shared/interfaces'
import type { UsePlayer } from '~/widgets/Player'

import { useUnit } from 'effector-react'
import { useCallback, useEffect, useState } from 'react'
import { model } from '~/entities/player'
import { RewindTypes } from '~/shared/constants/rewind'
import {
  ONE_DAY_IN_SEC,
  ONE_HOUR_IN_SEC,
  TWO_HOURS_SEC,
} from '~/shared/constants/time'
import {
  getCorrectTimeshiftTime,
  getLowestStartTimeDvr,
  getLowestStartTimeRewind,
  getSeekTimePlayerApiDvr,
  getSeekTimePlayerApiRewind,
  isRewindableDurationLow,
  normalizeTargetWithProgram,
  toSeconds,
} from './lib'

type TFetchRewindUrl = (
  _: Pick<
    IRewindMediaUrlParams,
    'rewindType' | 'urlStartTime' | 'targetStartTime'
  >
) => void

type TFetchUrl = () => void

export type TOnGoLive = (event: any) => void

export const useRewindCallbacks = ({
  enabled,
  mediaUrlData,
  contentKey,
  liveProgressBar,
  rewindState,
  fetchRewoundUrl,
  fetchUrl,
  player,
  isStorageDurAsMaxRewind = false,
}: {
  enabled: boolean
  mediaUrlData:
    | CurrentTvMediaUrlWithEpg
    | CurrentEventMediaUrlWithEpg
    | CurrentProgramMediaUrl
    | null
  contentKey: number | undefined
  liveProgressBar: Events.Media.ProgressBarChanged
  rewindState: IRewindState
  fetchRewoundUrl: TFetchRewindUrl
  fetchUrl: TFetchUrl
  player: UsePlayer
  isStorageDurAsMaxRewind?: boolean
}): {
  onLiveRewind: (value: number | undefined) => void
  onGoLive: TOnGoLive
  isRewindPending: boolean
} => {
  const isUrlRequestPending = useUnit(model.$isUrlRequestPending)

  const maxRewindLengthMs = isStorageDurAsMaxRewind
    ? (mediaUrlData?.liveRewindOptions?.storageDurationSec || ONE_HOUR_IN_SEC) *
      1000
    : (mediaUrlData?.liveRewindOptions?.rewindLengthSec || TWO_HOURS_SEC) * 1000

  const { isRewound, isRewindDelayed, rewindDetails, setIsRewindDelayed } =
    rewindState
  const { rewindType, rewoundUrlStartSec, rewoundTargetStartSec } =
    rewindDetails

  const { instance, events } = player

  const [isSeekExpected, setIsSeekExpected] = useState<boolean>(false)
  const [isSeeked, setIsSeeked] = useState<boolean>(true)
  const [shift, setShift] = useState<number>(0)

  useEffect(() => {
    setIsSeekExpected(false)
    setIsSeeked(true)
    setShift(0)
  }, [contentKey])

  const playerApiSeek = useCallback(
    (val: number | ((time: number) => number)) => {
      /* number -> should be a stamp in sec */
      if (!instance) return

      const newCurrentTime = Number(
        typeof val === 'function' //
          ? val(instance.currentTime() || 0)
          : val
      )

      return instance.currentTime(newCurrentTime)
    },
    [instance]
  )

  const rewindApiSeek = useCallback(
    (urlStartTime: number, targetStartTime: number) => {
      // console.log('🚀 rewind with new url to', new Date(targetStartTime * 1000))
      setIsSeekExpected(true)
      fetchRewoundUrl?.({ rewindType, urlStartTime, targetStartTime })
    },
    [fetchRewoundUrl, rewindType]
  )

  const onLiveRewindWithShift = useCallback(
    (shift: number) => {
      setIsRewindDelayed(false)
      setShift(shift)

      switch (rewindType) {
        case RewindTypes.dvr:
          if (isRewound)
            playerApiSeek(
              (currentPlaybackPosition) => currentPlaybackPosition + shift
            )
          else
            rewindApiSeek(
              getLowestStartTimeDvr(liveProgressBar, maxRewindLengthMs),
              toSeconds(Date.now()) + shift
            )
          break
        case RewindTypes.rewind:
          if (isRewound)
            playerApiSeek(
              (currentPlaybackPosition) => currentPlaybackPosition + shift
            )
          else
            rewindApiSeek(
              getLowestStartTimeRewind(liveProgressBar, maxRewindLengthMs),
              toSeconds(Date.now()) + shift
            )
          break
        case RewindTypes.timeshift: {
          /* magic number lower helps to seek when chunk (9s) almost equal seek step (10s) */
          shift = shift > 0 && isRewound ? shift : shift - 2

          const targetTimeSec = isRewound
            ? Math.round(
                (instance?.currentTime() || 0) + rewoundTargetStartSec + shift
              )
            : toSeconds(Date.now()) + shift

          const correctedTime = getCorrectTimeshiftTime(
            liveProgressBar,
            targetTimeSec
          )

          rewindApiSeek(correctedTime, correctedTime)
          break
        }
      }
    },
    [
      instance,
      isRewound,
      liveProgressBar,
      playerApiSeek,
      rewindApiSeek,
      maxRewindLengthMs,
      rewindType,
      rewoundTargetStartSec,
      setIsRewindDelayed,
    ]
  )

  const onLiveRewindWithStamp = useCallback(
    (targetTimeSec: number) => {
      setIsRewindDelayed(false)

      targetTimeSec = normalizeTargetWithProgram({
        liveProgressBar,
        targetTimeSec,
      })

      switch (rewindType) {
        case RewindTypes.dvr:
          if (isRewound) {
            playerApiSeek(
              getSeekTimePlayerApiDvr(liveProgressBar, targetTimeSec)
            )
          } else {
            rewindApiSeek(
              getLowestStartTimeDvr(liveProgressBar, maxRewindLengthMs),
              targetTimeSec
            )
          }
          break
        case RewindTypes.rewind:
          if (isRewound) {
            playerApiSeek(
              getSeekTimePlayerApiRewind(rewoundUrlStartSec, targetTimeSec)
            )
          } else {
            rewindApiSeek(
              getLowestStartTimeRewind(liveProgressBar, maxRewindLengthMs),
              targetTimeSec
            )
          }
          break
        case RewindTypes.timeshift: {
          const correctedTime = getCorrectTimeshiftTime(
            liveProgressBar,
            targetTimeSec
          )

          rewindApiSeek(correctedTime, correctedTime)
          break
        }
      }
    },
    [
      isRewound,
      liveProgressBar,
      playerApiSeek,
      rewindApiSeek,
      maxRewindLengthMs,
      rewindType,
      rewoundUrlStartSec,
      setIsRewindDelayed,
    ]
  )

  /**
   * This callback seeks, depends on rewind type
   */
  const onLiveRewind = useCallback(
    (value?: number) => {
      if (!enabled || !rewindType || !liveProgressBar || !value) return // no absolutely necessary data

      if (isRewindableDurationLow(liveProgressBar)) return

      /*
       * Small 'value' presents relative shift in seconds,
       * big 'value' presents absolute stamp in seconds
       */
      const isRelative = value < ONE_DAY_IN_SEC

      if (isRelative) return onLiveRewindWithShift(value)

      return onLiveRewindWithStamp(value)
    },
    [
      enabled,
      liveProgressBar,
      onLiveRewindWithShift,
      onLiveRewindWithStamp,
      rewindType,
    ]
  )

  useEffect(() => {
    if (!enabled) return

    // this effect tracks moment - fetching of rewind url started
    if (isUrlRequestPending) {
      setIsSeeked(false)
    }
  }, [enabled, isUrlRequestPending])

  events.on?.('loadedmetadata:rewind', () => {
    if (!enabled) return

    const isTimeShift = rewindType === RewindTypes.timeshift

    if (!isSeeked && isSeekExpected) {
      // Video url is fetched and player rewind url is ready and seek happened
      setIsSeeked(true)
      setIsSeekExpected(false)
      setShift(0)

      /* all links starts to play from 'live' but
       * - timeshift needs to play from start
       * - dvr/rewind play from target stamp
       *
       * Seek as soon as 'loadedmetadata' fires (seekable() is already valid)
       * rather than waiting for 'canplaythrough' - by then the tech has
       * already started decoding/painting frames at the manifest's default
       * (live) position, which is visible as a live-frame blink before this
       * corrective seek used to land.
       */

      if (!instance) return

      if (isRewound) {
        const ranges = instance.seekable() as unknown as TimeRanges | undefined

        if (isTimeShift) {
          instance.currentTime(ranges?.start(0))
        } else {
          // More accurate calculation of new position in rewind using shift
          if (shift) {
            instance.currentTime((ranges?.end(0) || 0) + shift)
          }

          // best for progress bar clicks
          else {
            instance.currentTime(rewoundTargetStartSec - rewoundUrlStartSec)
          }
        }
      }
    }
  })

  const onGoLive = useCallback(() => {
    setIsSeekExpected(false)

    if (!isRewound && isRewindDelayed) {
      // not in actual rewind, and goLive pressed
      setIsRewindDelayed(false)
      return instance?.play()
    }

    // common case
    fetchUrl()
    setIsRewindDelayed(false)
  }, [isRewound, isRewindDelayed, instance, fetchUrl, setIsRewindDelayed])

  return {
    onLiveRewind,
    onGoLive,
    isRewindPending: !isSeeked && isSeekExpected,
  }
}
